import { CITIZENSHIPS_MOCK } from './citizenships.mock';
import {
  AgentApiCitizenship,
  AgentApiErrorPayload,
  AgentClientPatch,
  AgentClientResponse,
  AgentClientsResponse,
  AgentCredentials,
  AgentLeadPatch,
  AgentLeadResponse,
  AgentLeadsResponse,
  AgentOrderResponse,
  AgentOrdersResponse,
  AgentSession,
  AgentTouristDraft,
  AgentTouristPatch,
} from './types';

export const getAgentApiBase = () =>
  `${window.location.origin}/api/agent/v1`;

export class AgentApiError extends Error {
  status: number;

  code: string;

  fields?: Record<string, string[]>;

  constructor(status: number, payload: AgentApiErrorPayload = {}) {
    const code = payload.error?.code || 'unknown_error';

    super(code);
    this.name = 'AgentApiError';
    this.status = status;
    this.code = code;
    this.fields = payload.error?.fields;
  }
}

const buildQuery = (
  params: Record<string, string | number | boolean | undefined>
) => {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') query.set(key, String(value));
  });

  const result = query.toString();
  return result ? `?${result}` : '';
};

const requestAgentApi = async <T>(
  path: string,
  options: RequestInit = {}
): Promise<T> => {
  const response = await window.fetch(`${getAgentApiBase()}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  const payload = response.status === 204 ? null : await response.json();

  if (!response.ok) {
    throw new AgentApiError(response.status, payload || {});
  }

  return payload as T;
};

export const getAgentSession = () =>
  requestAgentApi<AgentSession>('/session');

export const loginAgent = (credentials: AgentCredentials) =>
  requestAgentApi<AgentSession>('/session', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });

export const loginAgentWithStaffCode = (staffCode: string) =>
  requestAgentApi<AgentSession>('/session', {
    method: 'POST',
    body: JSON.stringify({ staff_code: staffCode }),
  });

export const requestAgentLogout = () =>
  requestAgentApi<void>('/session', { method: 'DELETE' });

export const getAgentOrders = ({
  query,
  page = 1,
  perPage = 20,
}: {
  query?: string;
  page?: number;
  perPage?: number;
} = {}) =>
  requestAgentApi<AgentOrdersResponse>(
    `/orders${buildQuery({ query, page, per_page: perPage })}`
  );

export const getAgentOrder = (id: string) =>
  requestAgentApi<AgentOrderResponse>(`/orders/${id}`);

export const getAgentClients = ({
  query,
  page = 1,
  perPage = 20,
}: {
  query?: string;
  page?: number;
  perPage?: number;
} = {}) =>
  requestAgentApi<AgentClientsResponse>(
    `/clients${buildQuery({ query, page, per_page: perPage })}`
  );

export const getAgentClient = (id: string) =>
  requestAgentApi<AgentClientResponse>(`/clients/${id}`);

export const getAgentLeads = ({
  status,
  mine,
  beforeId,
  perPage = 20,
}: {
  status?: string;
  mine?: boolean;
  beforeId?: number;
  perPage?: number;
} = {}) =>
  requestAgentApi<AgentLeadsResponse>(
    `/leads${buildQuery({
      status,
      mine,
      before_id: beforeId,
      per_page: perPage,
    })}`
  );

export const getAgentLead = (id: string) =>
  requestAgentApi<AgentLeadResponse>(`/leads/${id}`);

export const takeAgentLead = (id: string) =>
  requestAgentApi<AgentLeadResponse>(`/leads/${id}/take`, { method: 'POST' });

export const updateAgentLead = (id: string, lead: AgentLeadPatch) =>
  requestAgentApi<AgentLeadResponse>(`/leads/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ lead }),
  });

export const updateAgentClient = (id: string, client: AgentClientPatch) =>
  requestAgentApi<AgentClientResponse>(`/clients/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ client }),
  });

export const createAgentOrderTourist = (
  orderId: string,
  tourist: AgentTouristDraft
) =>
  requestAgentApi<{ tourist: import('./types').AgentApiTourist }>(
    `/orders/${orderId}/tourists`,
    {
      method: 'POST',
      body: JSON.stringify({ tourist }),
    }
  );

export const updateAgentOrderTourist = (
  orderId: string,
  touristId: string,
  tourist: AgentTouristPatch
) =>
  requestAgentApi<{ tourist: import('./types').AgentApiTourist }>(
    `/orders/${orderId}/tourists/${touristId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ tourist }),
    }
  );

export const deleteAgentOrderTourist = (orderId: string, touristId: string) =>
  requestAgentApi<void>(`/orders/${orderId}/tourists/${touristId}`, {
    method: 'DELETE',
  });

// Ручки со списком гражданств на бэке пока нет — отдаём мок; потом заменить на запрос к API.
export const getAgentCitizenships = async (): Promise<AgentApiCitizenship[]> =>
  CITIZENSHIPS_MOCK;
